import { useMemo } from 'react';
import type { PrintRunRecord } from '../types';
import { normalizeCoaKeys } from '../wysiwyg/coaUtils';

const positiveId = (value: unknown): number | null => {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && !/^\d+$/.test(value)) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

/** An issued print renders only the snapshot validated for that run by the API. */
export function decodeIssuedPrintCoa(
  run: PrintRunRecord | null | undefined,
  printRunId: unknown,
  packageId: unknown,
): Record<string, any> | null {
  const id = positiveId(printRunId);
  const activeId = positiveId(packageId);
  const snapshot = run?.outputCoaSnapshot;
  if (!id || !activeId || positiveId(run?.id) !== id || positiveId(run?.packageId) !== activeId
    || !snapshot || positiveId(snapshot.printRunId) !== id || positiveId(snapshot.packageId) !== activeId
    || snapshot.data == null) return null;
  const coaId = snapshot.packageCoaId == null ? null : positiveId(snapshot.packageCoaId);
  if (snapshot.packageCoaId != null && coaId == null) return null;
  try {
    const data: unknown = typeof snapshot.data === 'string' ? JSON.parse(snapshot.data) : snapshot.data;
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
    const payload = data as Record<string, unknown>;
    const output: Record<string, unknown> = { ...payload, coaId };
    if (run?.sourceName === 'Multi-Package Print') {
      const explicitTag = typeof payload.packageTag === 'string' && payload.packageTag.trim()
        ? payload.packageTag : null;
      const nodeTag = typeof payload.id === 'string' && payload.id.trim() ? payload.id : null;
      if (Object.prototype.hasOwnProperty.call(payload, 'packageTag') && !explicitTag) return null;
      if (explicitTag && nodeTag && explicitTag !== nodeTag) return null;
      // Node COAs store the tag in id. Only child output needs this alias;
      // keep ordinary snapshots and the frozen payload unchanged.
      const packageTag = explicitTag ?? nodeTag;
      if (packageTag) output.packageTag = packageTag;
    }
    // An archived staged snapshot has no live package COA ID.
    return normalizeCoaKeys(output);
  } catch {
    return null;
  }
}

/** Issued staged output uses the owned snapshot selected by the print-run API. */
export function decodeIssuedStagedPrintCoa(
  run: PrintRunRecord | null | undefined,
  printRunId: unknown,
): Record<string, any> | null {
  const id = positiveId(printRunId);
  const context = run?.stagedPrintContext;
  const coaId = positiveId(context?.stagedCoaId);
  if (!id || positiveId(run?.id) !== id || run?.packageId != null
    || !context || !coaId || positiveId(run?.stagePackageCoaId) !== coaId
    || !positiveId(context.facilityId) || typeof context.packageLabel !== 'string'
    || typeof run.packageLabel !== 'string' || !context.packageLabel.trim()
    || context.packageLabel.toLowerCase() !== run.packageLabel.toLowerCase()
    || typeof context.stagedCoaData !== 'string') return null;
  try {
    const data: unknown = JSON.parse(context.stagedCoaData);
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
    return normalizeCoaKeys({ ...data, coaId });
  } catch {
    return null;
  }
}

export default function useIssuedPrintCoa(
  run: PrintRunRecord | null | undefined,
  printRunId: unknown,
  packageId: unknown,
) {
  return useMemo(() => decodeIssuedPrintCoa(run, printRunId, packageId), [run, printRunId, packageId]);
}

export function useIssuedStagedPrintCoa(
  run: PrintRunRecord | null | undefined,
  printRunId: unknown,
) {
  return useMemo(() => decodeIssuedStagedPrintCoa(run, printRunId), [run, printRunId]);
}
